import $ from 'jquery';

import { getDisyNamespace } from '@disy/lib/disy';
import { on } from '@disy/lib/event-util';
import { getCadenzaTopWindow } from '@disy/lib/iframe';
import type { Teardown } from '@disy/lib/teardown';

import { PARAMETER_EMBEDDING_TARGET_NAME } from 'cadenza/api-client/api-constants';
import { isFeatureAvailable } from 'cadenza/features';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { navigateWithoutConfirmation } from 'cadenza/utils/confirm-navigation-util';
import { getLocation } from 'cadenza/utils/location';

const SESSION_POLLING_URL = '/session-polling';
const KEEP_ALIVE_PARAM = 'keepAlive';
const INVALID_SESSION_REDIRECT_URL = '/login?loginStatus=invalidSession';
const INVALID_SESSION_REDIRECT_URL_GUEST = '/guest-login?loginStatus=invalidSession';
const INVALID_SESSION_REDIRECT_URL_EMBEDDING = '/embedding-standby';

let forcedKeepAlive = false;

/**
 * Periodically polls the backend whether the Cadenza session is still alive.
 * If not (i.e. if the polling requests fails), the user is redirected to the login page
 * or to the embedding standby page.
 *
 * By default, a polling request (unlike other requests) does not keep the session alive.
 * But if the user is interacting with the page, we include a keep alive parameter.
 * That way, the session is kept alive also when the user does not cause requests for some time.
 * (The maximum session timeout is still respected of course.)
 *
 * The session keep alive can also be forced with {@link keepSessionAlive}.
 *
 * @see `PollAwareSessionTimeoutFilter.java`
 */
export function setupSessionPoller(): Teardown[] {
  // Poll the session only in the Cadenza top window and (obviously) only
  // when there actually is a session (e.g. not on the login page or the embedding-standby page).
  if (window !== getCadenzaTopWindow() || !getDisyNamespace().clientSessionId) {
    return [];
  }
  let keepAlive = false;
  const pollingInterval = getDisyNamespace().cadenzaSessionPollingInterval!;
  return [
    setInterval(async () => {
      try {
        await pollCadenzaSession(forcedKeepAlive || keepAlive);
        keepAlive = false;
      } catch {
        handleCadenzaSessionTimeOut();
      }
    }, pollingInterval),
    on(document, ['pointerdown', 'pointermove', 'keydown', 'mousewheel'], () => {
      keepAlive = true;
    }),
    () => keepSessionAlive(false),
  ];
}

function pollCadenzaSession(keepAlive: boolean) {
  return Promise.resolve($.ajax(cadenzaUrl(SESSION_POLLING_URL) + (keepAlive ? `?${KEEP_ALIVE_PARAM}` : ''))).catch(
    (xhr) => {
      if (xhr.status >= 400 && xhr.status < 500) {
        throw xhr; // Fail only for 4xx, so that we do not redirect e.g. for network outages.
      }
    },
  );
}

function handleCadenzaSessionTimeOut() {
  navigateWithoutConfirmation(cadenzaUrl(getInvalidSessionRedirectUrl()));
}

function getInvalidSessionRedirectUrl() {
  const isEmbeddedContent = new URL(getLocation().href).searchParams.has(PARAMETER_EMBEDDING_TARGET_NAME);
  if (isEmbeddedContent) {
    return INVALID_SESSION_REDIRECT_URL_EMBEDDING;
  }
  const isGuest = getDisyNamespace().currentUser == null;
  return isGuest && isFeatureAvailable('GUEST_LOGIN_PAGE') ?
      INVALID_SESSION_REDIRECT_URL_GUEST
    : INVALID_SESSION_REDIRECT_URL;
}

/** Whether to force the session keep alive */
export function keepSessionAlive(force: boolean) {
  forcedKeepAlive = force;
}
